import { AuthenticationResult } from '@feathersjs/authentication';
import { FeathersService } from '@feathersjs/feathers';
import { isAfter } from 'date-fns';
import { Location } from 'history';
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import toast from 'react-hot-toast';
import i18next from 'src/i18n';
import { IGroupUser, ISpaceUser, Services, UserRole } from 'src/types';
import { RoutingStore, SpaceStore } from '.';
import client from '../feathers';
import { User } from '../models';
import { getRoutePath, Page } from '../routes';


type AccountActionResponse = "success" | "error" | "no_token" | "invalid_token";
type ForgotPasswordRequestResponse = "no_account_found";
type ResetPasswordRequestResponse = "no_password";

export class AuthStore  {
  protected readonly invite_service:  FeathersService<any>      = client.service(Services.Invite);
  protected readonly account_service: FeathersService<any>      = client.service(Services.Account);
  protected readonly user_service:    FeathersService<any>      = client.service(Services.Users);
  private token_expiration: Date = null;

  user:             User   | null = null; // Logged in user
  error_message:    string | null = null;
  isAuthenticating: boolean       = true;
  socketId: string                = null;


  constructor(public routingStore: RoutingStore) {
    // Note: This will not take care of async calls. So everything that might be called async has to be wrapped in runInAction() to avoid warnings 
    makeObservable(this, {
      user:              observable,
      error_message:     observable,
      isAuthenticating:  observable,
      
      setAuth:        action,
      reAuthenticate: action,
      login:          action,
      logout:         action,
      
      getCurUser:        computed,
      isLeader:          computed,
      isLoggedIn:        computed,
      getCurWorkspaceID: computed,
      getCurRoomID:      computed,
      getCurProjectID:   computed,
      getCurSpaceID:     computed,
    });

    // Check JWT to re-log in if possible
    this.reAuthenticate();

    // Get the notification about connect 
    client.service(Services.Session).on('connect', (data: any) =>  {
      // console.log('Get the notification about connect', data);
    });

    // Logout coming 
    client.service(Services.Session).on('logout', () =>  {
      // console.log('you are logged out!')
      this.logout();
    });

  }
  
  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  init  = () => {}
  clear = () => {}
  /*******************************************************************************************************************************************
   * Getter 
   ********************************************************************************************************************************************/
  get getCurUserID():      number  { return this.getCurUser?.id; }
  get getCurUser():        User    { return this.user; }
  get isLeader():          boolean { return [UserRole.Leader, UserRole.Owner].includes(this.getCurUser?.getRole(this.getCurRoomID)); }
  get isLoggedIn():        boolean { return Boolean(this.user) && this.token_expiration && isAfter(this.token_expiration, new Date()); }
  get hasWorkspace():      boolean { return Boolean(this.getCurWorkspaceID); }
  get getCurWorkspaceID(): number  { return this.user?.workspace_id; }
  get getCurProjectID():   number  { return this.user?.project_id; }
  get getCurRoomID():      number  { return this.user?.room_id; }
  get getCurSpaceID():     number  { return this.getCurRoomID || this.getCurProjectID || this.getCurWorkspaceID; }
  
  /*******************************************************************************************************************************************
   * Setter 
   ********************************************************************************************************************************************/

  setAuth = runInAction(() => (res: AuthenticationResult) => {
    if ("user" in res) {
      
      this.user = User.create({
        ...res.user
      });
    
      i18next.changeLanguage(this.user.language);

      if(!this.user.workspace_id) {
        this.routingStore.push(getRoutePath(Page.Workspaces));
      } else {
        const location: Location = this.routingStore.location as Location;
        if (location && location.state) {
          this.routingStore.history.push(location.state);
        } else { 
        /* Reroute to dashboard */ }
        // TODO Set user language // i18next.changeLanguage(this.user.language || "en");
      }
      this.token_expiration = new Date(res.authentication.payload.exp * 1000);
    } else {
      console.error(res);
    }
    this.isAuthenticating = false
  });

  /*******************************************************************************************************************************************
   * Actions 
   ********************************************************************************************************************************************/

  reAuthenticate = async (): Promise<void> => {
    try {
      this.isAuthenticating = true;
      const res: AuthenticationResult = await client.reAuthenticate();
      this.setAuth(res);
      toast.success(i18next.t("welcome_back", {user: this.user.first_name}))
      this.checkUserProfile();
    } catch (err) {
      console.error(err);
    }
    runInAction(() => this.isAuthenticating = false);
  }

  login = async (email: string, password: string): Promise<boolean> => {
    runInAction(() => this.error_message = '');
    if (!email || !password) {
      runInAction(() => this.error_message = 'Login requires an email and a password')
      return false;
    }
    try {
      runInAction(() => this.isAuthenticating = true);
      const res: AuthenticationResult = await client.authenticate({
        strategy: 'local',
        email,
        password
      });
      this.setAuth(res);
      toast.success(i18next.t("welcome", {user: this.user.first_name}))
      this.checkUserProfile();
      return true;
    } catch (err) {
      console.error(err);
      if (err.message == 'Invalid login') {
        runInAction(() => this.error_message = (i18next.t('invalid_login')) )
      }
    }
    runInAction(() => this.isAuthenticating = false);
    return false;
  }

  signUp = async (email: string, password: string): Promise<boolean> => {
    try {
      const result = await this.user_service.create({ email, password });
      console.log('signup', result)
      await this.account_service.create({
        user_id: result.id,
        action: 'verify_workspace',
      });
      await this.login(email, password);
      this.routingStore.push(getRoutePath(Page.UserProfile));
      return true;
    } catch (error) {
      console.error(error)
      return false;
    }
  }

  logout = async (): Promise<boolean> => {
    this.user = null;
    this.token_expiration = null;
    await client.logout();
    client.get('storage').removeItem('feathers-jwt'); // We want to remove this one now;
    SpaceStore.clearSpaceCookies();
    return this.isLoggedIn;
  }

  requestForgotPassword = async (email: string): Promise<AccountActionResponse | ForgotPasswordRequestResponse> => {
    try {
      const result = await this.account_service.create({
        email: email,
        action: 'forgot_password',
      });
      return result;
    } catch (error) {
      console.error('requestForgotPassword', error);
      return 'error';
    }
  }

  resendInvite = async (user: User): Promise<any> => {
    try {
      await this.invite_service.create({
        action:       'resend',
        user_id:      user.id,
        workspace_id: user.workspace_id,
      });
      toast.success(i18next.t('invite_resent_to', {email: user.email}))
    } catch (error) {
      console.error('Failed to resend invite to user', error);
      toast.success(i18next.t("failed_to_send_to", {email: user.email}))
      return error;
    }
  }

  acceptInvite = async (token: string, password: string): Promise<any> => {
    try {
      const result = await this.invite_service.patch(null, {
        action:   'accept',
        token:    token,
        password: password,
      });
      return result;
    } catch (error) {
      console.error('Failed to accept invite for user', error);
      return error;
    }
  }

  requestResetPassword = async (token: string, password: string): Promise<AccountActionResponse | ResetPasswordRequestResponse> => {
    try {
      const result = await this.account_service.create({
        token:    token,
        password: password,
        action:   'reset_password',
      });
      return result;
    } catch (error) {
      console.error('requestResetPassword', error);
      return 'error';
    }
  }

  verifyAccount = async (token: string): Promise<AccountActionResponse> => {
    try {
      const result = await this.account_service.create({
        token:  token,
        action: 'verify_account',
      });
      return result;
    } catch (error) {
      console.error(error);
      return 'error';
    }
  }

  inviteUser = async (user: User, spaces: ISpaceUser[], groups: IGroupUser[]): Promise<boolean> => {
    runInAction(() => this.error_message = null);
    try {
      await user.save();
      await Promise.all(spaces.map(s => client.service("space-users").create({ ...s, user_id: user.id })));
      const saveGroupRelations = groups.map(g => {
        g.user_id = user.id
        return client.service("group-users").create(g);
      });
      await Promise.all(saveGroupRelations);
      return true;
    } catch (error) {
      console.error(error)
      this.error_message = error.message;
      return false;
    }
  }

  /**
   * Check if the profile of the current user is complete, i.e. 
   * - First/Last Name,
   * - Initials,
   * - Title,
   * - Phone Number and
   * - Company Name
   * are provided. If that is not the case, show a toaster to remind the user
   * about completing their profile. 
   */
  checkUserProfile = () => {
    if(
      !this.user.first_name || 
      !this.user.last_name || 
      !this.user.phone || 
      !this.user.avatar || 
      !this.user.company_name 
    ) {
      toast.error(i18next.t("profile_information_missing"), {duration: 10000})
    }
  };
}
